/**
 * FeatureGate — Conditional Wrapper für Premium-Inhalte.
 *
 * Usage:
 *   <FeatureGate feature="bisremote_cloud">
 *     <MyReportsPage />
 *   </FeatureGate>
 *
 * Ohne Feature: zeigt UpgradeBanner mit CTA → /my/shop.
 * Während Lizenz-Check noch lädt: Lade-Skeleton (kein flackerndes Banner).
 */

import { useFeatureGate } from '../../hooks/useFeatureGate';
import { UpgradeBanner } from './UpgradeBanner';

interface FeatureGateProps {
  feature: string;
  /** Optional eigene Fallback-UI statt Standard-Banner. */
  fallback?: React.ReactNode;
  /** Während des initialen Loadings — default Skeleton. */
  loadingFallback?: React.ReactNode;
  /** Optional: Banner-Titel und -Text überschreiben. */
  bannerTitle?: string;
  bannerDescription?: string;
  children: React.ReactNode;
}

export function FeatureGate({
  feature,
  fallback,
  loadingFallback,
  bannerTitle,
  bannerDescription,
  children,
}: FeatureGateProps) {
  const { loading, hasFeature, requiredAddonFor } = useFeatureGate();

  if (loading) {
    return (
      <>
        {loadingFallback ?? (
          <div className="animate-pulse rounded-2xl bg-gray-100 p-8 text-gray-400">
            Lizenz wird geprüft...
          </div>
        )}
      </>
    );
  }

  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return (
    <UpgradeBanner
      feature={feature}
      requiredAddon={requiredAddonFor(feature)}
      title={bannerTitle}
      description={bannerDescription}
    />
  );
}
